import { Trans } from '@lingui/macro';
import { Link, useNavigate } from "react-router-dom"
import { Button } from "@shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@shared/ui/card"
import { useAuth } from "@shared/hooks/useAuth"
import { Loader2, AlertCircle, User, Users, Sparkles } from "lucide-react"
import { Alert, AlertDescription } from "@shared/ui/alert"
import { ThemeToggle } from "@shared/ui/theme-toggle"
import { useEffect, useState } from "react"

function App() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Handle hash fragment redirects for password reset/recovery and invite links
  useEffect(() => {
    if (window.location.hash) {
      const hashString = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash;
      const hashParams = new URLSearchParams(hashString);
      const type = hashParams.get('type');
      const accessToken = hashParams.get('access_token');

      // Handle password recovery links - redirect to reset password page with the hash
      if (type === 'recovery' && accessToken) {
        setIsRedirecting(true);
        // Navigate to reset password page with the hash fragment preserved
        navigate(`/auth/reset-password${window.location.hash}`, { replace: true });
        return;
      }

      // Handle invite links - redirect to callback with the hash
      if (type === 'invite' && accessToken) {
        setIsRedirecting(true);
        navigate(`/auth/callback${window.location.hash}`, { replace: true });
        return;
      }

      // Handle magic link authentication
      if (type === 'magiclink' && accessToken) {
        setIsRedirecting(true);
        navigate(`/auth/callback${window.location.hash}`, { replace: true });
        return;
      }

      // Handle signup confirmation
      if (type === 'signup' && accessToken) {
        setIsRedirecting(true);
        navigate(`/auth/callback${window.location.hash}`, { replace: true });
        return;
      }
    }
  }, [navigate]);

  // If user is authenticated, redirect to the unified dashboard
  useEffect(() => {
    if (user && !isRedirecting && !window.location.hash) {
      setIsRedirecting(true);
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Show loading while redirecting to prevent flash
  if (isRedirecting) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center p-8 bg-gradient-to-br from-background via-background to-muted/20">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="p-8">
            <div className="flex flex-col items-center space-y-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <div className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  <Trans id='App.processing-your-request'>Processing your request</Trans>
                </div>
              </div>
              <div className="text-center space-y-3 w-full">
                <p className="text-sm text-muted-foreground">
                  <Trans id='App.redirecting-please-wait'>Redirecting, please wait...</Trans>
                </p>
                <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_100%] animate-[shimmer_2s_linear_infinite]"></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center p-8 bg-gradient-to-br from-background via-background to-muted/20">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="p-8">
            <div className="flex flex-col items-center space-y-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <div className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent"><Trans id='App.initializing-jema-manager'>Initializing Jema Manager</Trans></div>
              </div>
              <div className="text-center space-y-3 w-full">
                <p className="text-sm text-muted-foreground"><Trans id='App.checking-your-authentication-status'>Checking your authentication status...</Trans></p>
                <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_100%] animate-[shimmer_2s_linear_infinite]"></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Helpful info while loading */}
        <div className="mt-6 max-w-md text-center">
          <Alert className="border-muted-foreground/20 bg-card/50 backdrop-blur-sm">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs"><Trans id='App.taking-longer-than-usual-check'>Taking longer than usual? Check your internet connection or try refreshing the page.</Trans></AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Show loading if user is authenticated but redirect hasn't happened yet
  if (user) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center p-8 bg-gradient-to-br from-background via-background to-muted/20">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="p-8">
            <div className="flex flex-col items-center space-y-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <div className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  <Trans id='App.redirecting-to-dashboard'>Redirecting to dashboard</Trans>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mobile-safe mobile-container flex flex-col items-center justify-center p-4 sm:p-8 bg-gradient-to-br from-background via-background to-primary/5 relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/4 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/4 w-96 h-96 rounded-full bg-accent/5 blur-3xl" />
      </div>
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-lg mx-4 shadow-2xl border-primary/10 backdrop-blur-sm bg-card/95">
        <CardHeader className="space-y-4 pb-4 px-4 sm:px-6">
          <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-primary via-accent to-primary shadow-lg flex items-center justify-center">
            <Sparkles className="h-8 w-8 sm:h-10 sm:w-10 text-primary-foreground animate-pulse" />
          </div>
          <div className="space-y-2">
            <CardTitle className="text-center text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent"><Trans id='App.welcome-to-jema-manager'>Welcome to Jema Manager</Trans></CardTitle>
            <CardDescription className="text-center text-sm sm:text-base max-w-md mx-auto px-2"><Trans id='App.a-full-service-dental-medical'>A full-service dental medical tourism platform connecting international patients with dental providers in Colombia.</Trans></CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 pt-2 px-4 sm:px-6">
          <div className="flex flex-col gap-3">
            <Button
              asChild
              size="lg"
              className="h-12 sm:h-14 text-sm sm:text-base shadow-md hover:shadow-lg transition-all group touch-target"
            >
              <Link to="/auth/patient/signin" className="flex items-center gap-3">
                <User className="h-4 w-4 sm:h-5 sm:w-5 group-hover:scale-110 transition-transform flex-shrink-0" />
                <div className="flex flex-col items-start min-w-0">
                  <span className="font-semibold"><Trans id='App.patient-sign-in'>Patient Sign In</Trans></span>
                  <span className="text-xs text-primary-foreground/80"><Trans id='App.access-your-care-journey'>Access your care journey</Trans></span>
                </div>
              </Link>
            </Button>
            <Button
              variant="outline"
              asChild
              size="lg"
              className="h-12 sm:h-14 text-sm sm:text-base shadow-sm hover:shadow-md transition-all border-2 group touch-target"
            >
              <Link to="/staff/login" className="flex items-center gap-3">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 group-hover:scale-110 transition-transform flex-shrink-0" />
                <div className="flex flex-col items-start min-w-0">
                  <span className="font-semibold"><Trans id='App.staff-doctor-portal'>Staff & Doctor Portal</Trans></span>
                  <span className="text-xs text-muted-foreground"><Trans id='App.manage-your-practice'>Manage your practice</Trans></span>
                </div>
              </Link>
            </Button>
          </div>
          <div className="pt-4 border-t border-border">
            <p className="text-xs text-center text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Trans id='App.patients-use-email-verification'><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />Patients use email verification</Trans></span>
              {" • "}
              <span className="inline-flex items-center gap-1"><Trans id='App.staff-use-password-authentication'><span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />Staff use password authentication</Trans></span>
            </p>
          </div>
        </CardContent>
      </Card>
      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-muted-foreground"><Trans id='App.2025-jema-manager-all-rights'>© 2025 Jema Manager. All rights reserved.</Trans></p>
      </div>
    </div>
  );
}

export default App
